import { useEffect, useMemo } from 'react';
import { Marker } from 'react-leaflet';
import L from 'leaflet';

// FDI category colours — matched to the `fdi-*` Tailwind tokens
// (tailwind.config.ts) and the thresholds in ForecastMap.fdiColorClass.
function fdiColor(fdi: number): string {
  if (fdi < 21) return '#2196f3'; // safe
  if (fdi < 46) return '#4caf50'; // moderate
  if (fdi < 61) return '#ffc107'; // dangerous
  if (fdi < 76) return '#ff9800'; // very dangerous
  return '#f44336'; // extreme
}

// Bigger ring for higher danger (36px at FDI 0 → ~78px at FDI 100+).
function fdiSize(fdi: number): number {
  return Math.round(36 + Math.min(Math.max(fdi, 0), 100) * 0.42);
}

const STYLE_ID = 'fdi-danger-ring-styles';
const STYLE_CSS = `
.fdi-danger-ring { position: relative; display: flex; align-items: center; justify-content: center; }
.fdi-danger-ring__pulse {
  position: absolute; inset: 0; border-radius: 9999px;
  background: var(--ring-color); opacity: 0.35;
  animation: fdi-ring-pulse 1.8s ease-out infinite;
}
.fdi-danger-ring__core {
  width: 26%; height: 26%; border-radius: 9999px;
  background: var(--ring-color);
  box-shadow: 0 0 0 2px rgba(255,255,255,0.85);
}
@keyframes fdi-ring-pulse {
  0%   { transform: scale(0.45); opacity: 0.5; }
  70%  { opacity: 0.08; }
  100% { transform: scale(1); opacity: 0; }
}
`;

/** Inject the ring keyframes once (module-global). */
function useRingStyles() {
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = STYLE_CSS;
    document.head.appendChild(el);
  }, []);
}

function buildRingIcon(fdi: number): L.DivIcon {
  const color = fdiColor(fdi);
  const size = fdiSize(fdi);
  const html = `
    <div class="fdi-danger-ring" style="--ring-color:${color}; width:${size}px; height:${size}px;">
      <span class="fdi-danger-ring__pulse"></span>
      <span class="fdi-danger-ring__core"></span>
    </div>`;
  return L.divIcon({
    className: 'fdi-danger-ring-icon',
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

interface FdiDangerRingProps {
  position: [number, number];
  fdi: number | null | undefined;
  show?: boolean;
}

/**
 * A pulsing ring around a map location, sized and coloured by the fire danger
 * index — an at-a-glance danger cue. Non-interactive so it never blocks map
 * clicks. Mount as a child of a react-leaflet `MapContainer`.
 */
export function FdiDangerRing({ position, fdi, show = true }: FdiDangerRingProps) {
  useRingStyles();
  const icon = useMemo(() => (fdi == null ? null : buildRingIcon(fdi)), [fdi]);

  if (!show || icon == null) return null;
  return <Marker position={position} icon={icon} interactive={false} />;
}

export default FdiDangerRing;
